import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Cart } from '../types/types';
import { useAuth } from '../auth/useAuth';
import dataService from '../data/dataService';

interface CartContextType {
  cart: Cart | null;
  loading: boolean;
  itemCount: number;
  refreshCart: () => Promise<void>;
  addToCart: (productId: number, quantity?: number) => Promise<void>;
  updateQuantity: (itemId: number, quantity: number) => Promise<void>;
  removeItem: (itemId: number) => Promise<void>;
  clearCart: () => Promise<void>;
  checkout: () => Promise<number | null>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, getAccessToken } = useAuth();
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dataService.setTokenProvider(getAccessToken);
  }, [getAccessToken]);

  const refreshCart = useCallback(async () => {
    if (!isAuthenticated) {
      setCart(null);
      return;
    }
    setLoading(true);
    try {
      const data = await dataService.getCart();
      setCart(data);
    } catch {
      setCart(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      refreshCart();
    } else {
      setCart(null);
    }
  }, [isAuthenticated, refreshCart]);

  const addToCart = useCallback(async (productId: number, quantity: number = 1) => {
    await dataService.addToCart(productId, quantity);
    await refreshCart();
  }, [refreshCart]);

  const updateQuantity = useCallback(async (itemId: number, quantity: number) => {
    await dataService.updateCartItem(itemId, quantity);
    await refreshCart();
  }, [refreshCart]);

  const removeItem = useCallback(async (itemId: number) => {
    await dataService.removeCartItem(itemId);
    await refreshCart();
  }, [refreshCart]);

  const clearCartAction = useCallback(async () => {
    await dataService.clearCart();
    await refreshCart();
  }, [refreshCart]);

  const checkout = useCallback(async (): Promise<number | null> => {
    try {
      const order = await dataService.checkout();
      await refreshCart();
      return order.id;
    } catch {
      return null;
    }
  }, [refreshCart]);

  const itemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) ?? 0;

  return (
    <CartContext.Provider value={{
      cart, loading, itemCount, refreshCart,
      addToCart, updateQuantity, removeItem,
      clearCart: clearCartAction, checkout,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export function useCart(): CartContextType {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
}
